<script setup lang="ts">
import { useResizeObserver } from '@vueuse/core';
import { useField } from 'vee-validate';
import { computed, nextTick, ref, useId, useTemplateRef, watch } from 'vue';

const emit = defineEmits<{
  'update:value': [value: string];
  commit: [value: string];
}>();

const inputDomId = useId();
const errorDomId = useId();

const props = withDefaults(
  defineProps<{
    ariaLabel?: string;
    autoResize?: boolean;
    autofocus?: boolean;
    commitOnEnter?: boolean;
    disabled?: boolean;
    fluid?: boolean;
    label?: string;
    maxErrors?: number;
    name: string;
    placeholder?: string;
    readonly?: boolean;
    maxlength?: number;
    rows?: number;
    theme?: 'inline' | 'rounded' | 'square' | 'surface';
    validateOnInput?: boolean;
    value?: string;
    fillHeightOfParent?: boolean;
    extraPadding?: boolean;
    textareaClass?: string;
  }>(),
  {
    ariaLabel: undefined,
    autoResize: false,
    autofocus: false,
    commitOnEnter: true,
    disabled: false,
    fluid: false,
    label: undefined,
    maxErrors: 100,
    maxlength: undefined,
    placeholder: undefined,
    readonly: false,
    rows: 6,
    theme: 'square',
    validateOnInput: true,
    value: undefined,
    fillHeightOfParent: false,
    extraPadding: false,
    textareaClass: undefined,
  },
);

const { errors, handleChange, meta, value } = useField(() => props.name, undefined, {
  initialValue: props.value ?? '',
});

const textareaRef = useTemplateRef('textareaRef');
const autoFocusInternal = ref<boolean>(false);
const isFocused = ref(false);
let skipNextBlurCommit = false;

const adjustTextareaHeight = async (): Promise<void> => {
  if (!props.autoResize && !props.fluid) return;
  await nextTick();
  const textarea = textareaRef.value;
  if (!textarea) return;

  textarea.style.height = 'auto';
  const borderHeight = textarea.offsetHeight - textarea.clientHeight;
  if (props.fillHeightOfParent && textarea.parentElement) {
    textarea.style.height = `${textarea.parentElement.scrollHeight + borderHeight}px`;
  } else {
    textarea.style.height = `${textarea.scrollHeight + borderHeight}px`;
  }
};

useResizeObserver(textareaRef, () => {
  void adjustTextareaHeight();
});

const errorsToShow = computed(() => errors.value.slice(0, props.maxErrors));

const hasErrors = computed(() => errorsToShow.value.length > 0);

const isLabelFloating = computed(() => {
  return isFocused.value || (value.value && value.value.length > 0);
});

const handleCommit = (): void => {
  if (props.readonly) return;
  emit('commit', value.value);
};

const handleEnter = (event: KeyboardEvent): void => {
  if (!props.commitOnEnter || event.shiftKey) return;

  skipNextBlurCommit = true;
  handleCommit();
  textareaRef.value?.blur();
  setTimeout(() => {
    skipNextBlurCommit = false;
  }, 0);
};

const handleBlur = (): void => {
  isFocused.value = false;

  if (skipNextBlurCommit) {
    skipNextBlurCommit = false;
    return;
  }
  handleCommit();
};

const handleInput = (event: Event): void => {
  handleChange(event, props.validateOnInput);
  emit('update:value', value.value);
  void adjustTextareaHeight();
};

watch(
  [
    () => props.value,
    () => props.autoResize,
    () => props.disabled,
    () => props.fillHeightOfParent,
    () => props.fluid,
    () => props.readonly,
  ],
  () => {
    handleChange(props.value, props.validateOnInput);
    void adjustTextareaHeight();
  },
  { immediate: true },
);

watch(
  () => props.autofocus,
  () => {
    if (props.autofocus) {
      (document.activeElement as HTMLElement | null)?.blur();
      autoFocusInternal.value = props.autofocus;
    }
  },
);

const focus = (): void => textareaRef.value?.focus();
const blur = (): void => textareaRef.value?.blur();

defineExpose({ textareaRef, focus, blur });
</script>

<template>
  <div :class="['flex flex-col gap-xs', fluid && 'h-full min-h-0']">
    <div class="relative h-full min-h-0">
      <label
        v-if="label"
        :for="inputDomId"
        :class="[
          'input-text-label',
          theme === 'square' && 'top-[25px]',
          theme === 'rounded' &&
            `text-primary font-semibold ${extraPadding ? 'top-[72px] left-xxl right-xxl' : 'top-8 left-lg right-lg '}`,
          isLabelFloating && theme === 'square' && '!label-floating',
          isFocused && theme === 'square' && 'text-tint',
        ]"
      >
        {{ label }}
      </label>

      <template v-if="disabled && !value?.trim()">
        <!-- nothing to show! -->
      </template>

      <p
        v-else-if="disabled || readonly"
        :class="[
          'input-text',
          'whitespace-pre-line break-words',
          !readonly && 'input-text-disabled',
          theme === 'rounded' && `rounded-badge h-full ${extraPadding ? 'px-xxl pb-xxl pt-26' : 'px-lg pb-lg pt-16'}`,
          theme === 'inline' && '!min-h-0 !border-transparent !bg-transparent',
          theme === 'surface' && '!rounded-md !border-transparent !bg-transparent !px-sm !py-sm',
        ]"
      >
        {{ value }}
      </p>

      <textarea
        v-else
        :id="inputDomId"
        ref="textareaRef"
        :name="name"
        :rows="rows"
        :class="[
          'input-text',
          hasErrors && 'input-text-error',
          label && 'input-text-labeled',
          fluid && 'h-full min-h-0 resize-none',
          autoResize && 'resize-none',
          theme === 'rounded' && `rounded-badge ${extraPadding ? 'px-xxl pb-xxl pt-26' : 'px-lg pb-lg pt-16'}`,
          theme === 'inline' && '!min-h-0 !border-transparent !bg-transparent',
          theme === 'surface' &&
            '!rounded-md !border-transparent !bg-transparent !px-sm !py-sm caret-tint transition-colors duration-300 hover:!bg-surface-tertiary focus:!bg-surface-secondary focus:hover:!bg-surface-secondary focus-visible:!outline-none placeholder:!text-secondary placeholder:!opacity-100',
          textareaClass,
        ]"
        :style="!fluid && { overflow: autoResize ? 'hidden' : 'auto' }"
        :autofocus="autoFocusInternal"
        :value="value"
        :maxlength="props.maxlength"
        :aria-label="ariaLabel"
        :aria-required="meta.required"
        :placeholder="placeholder"
        :aria-invalid="hasErrors"
        :aria-describedby="hasErrors ? errorDomId : undefined"
        :aria-errormessage="hasErrors ? errorDomId : undefined"
        @input="handleInput"
        @focus="isFocused = true"
        @blur="handleBlur"
        @keydown.enter="handleEnter"
      ></textarea>
    </div>

    <ul v-if="hasErrors" :id="errorDomId" class="text-sm text-right text-error">
      <li v-for="error of errorsToShow" :key="error" class="list-none">{{ error }}</li>
    </ul>
  </div>
</template>
